import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { Permission, PermissionGroups, PermissionMetadata, getPermissionCategories } from '../types/permissions.ts';
import { Settings, Play, RotateCcw, Eye, EyeOff } from 'lucide-react';

const PermissionSimulator: React.FC = () => {
  const { currentUser, startSimulation, stopSimulation, isSimulating, simulationInfo } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>([]);
  const [selectedPreset, setSelectedPreset] = useState<string>('');

  // Initialize with simulation info if available
  React.useEffect(() => {
    if (simulationInfo) {
      setSelectedPermissions(simulationInfo.permissions);
      setSelectedPreset(simulationInfo.preset);
    }
  }, [simulationInfo]);

  const handlePresetChange = (preset: string) => {
    setSelectedPreset(preset);
    if (preset && PermissionGroups[preset as keyof typeof PermissionGroups]) {
      setSelectedPermissions(PermissionGroups[preset as keyof typeof PermissionGroups]);
    }
  };

  const handleCustomPermissionToggle = (permission: Permission) => {
    const newPermissions = selectedPermissions.includes(permission)
      ? selectedPermissions.filter(p => p !== permission)
      : [...selectedPermissions, permission];
    
    setSelectedPermissions(newPermissions);
    setSelectedPreset('custom');
  };

  const resetToOriginal = () => {
    stopSimulation();
    setSelectedPermissions([]);
    setSelectedPreset('');
  };

  const handleStartSimulation = () => {
    if (selectedPermissions.length > 0) {
      startSimulation(selectedPermissions);
      setIsOpen(false);
    }
  };

  if (!currentUser?.permissions?.includes(Permission.ADMIN)) {
    return null; // Only show to admins
  }

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 left-4 bg-purple-600 hover:bg-purple-700 text-white p-3 rounded-full shadow-lg z-50 transition-all duration-200"
        title="Permission Simulator"
      >
        <Settings className="w-5 h-5" />
      </button>

      {/* Current Simulation Indicator */}
      {isSimulating && simulationInfo && (
        <div className="fixed top-4 right-4 bg-orange-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center space-x-2">
          <Eye className="w-4 h-4" />
          <span className="text-sm font-medium">
            Simulating: {simulationInfo.preset === 'custom' ? 'Custom' : simulationInfo.preset} 
            ({simulationInfo.permissions.length} permissions)
          </span>
          <button
            onClick={resetToOriginal}
            className="ml-2 bg-orange-600 hover:bg-orange-700 px-2 py-1 rounded text-xs"
            title="Reset to Original"
          >
            <RotateCcw className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Settings className="mr-2 h-5 w-5" />
                Permission Simulator
              </h2>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <EyeOff className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h3 className="font-medium text-blue-900 mb-2">How it works:</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Select a permission preset or create a custom set</li>
                  <li>• Click "Start Simulation" to temporarily test those permissions</li>
                  <li>• Navigate around the app to see what's accessible</li>
                  <li>• Click the reset button or refresh to restore admin permissions</li>
                </ul>
              </div>
            </div>

            <div className="space-y-6">
              {/* Permission Presets */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Quick Presets</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(PermissionGroups).map(([key, permissions]) => (
                    <button
                      key={key}
                      onClick={() => handlePresetChange(key)}
                      className={`p-4 border rounded-lg text-left transition-all ${
                        selectedPreset === key
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-sm mb-1">
                        {key.replace('_', ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                      </div>
                      <div className="text-xs text-gray-500">
                        {permissions.length} permissions
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Permission Selection */}
              {selectedPreset && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    Permissions for {selectedPreset === 'custom' ? 'Custom' : selectedPreset}
                  </h3>
                  <div className="space-y-4">
                    {getPermissionCategories().map(category => {
                      const categoryPermissions = Object.values(Permission).filter(
                        p => PermissionMetadata[p]?.category === category
                      );
                      
                      return (
                        <div key={category} className="border border-gray-200 rounded-lg">
                          <div className="bg-gray-50 px-4 py-2 font-medium text-sm">
                            {category}
                          </div>
                          <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                            {categoryPermissions.map(permission => {
                              const metadata = PermissionMetadata[permission];
                              const isSelected = selectedPermissions.includes(permission);
                              
                              return (
                                <label
                                  key={permission}
                                  className="flex items-center space-x-2 text-sm cursor-pointer hover:bg-gray-50 p-2 rounded"
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleCustomPermissionToggle(permission)}
                                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                  />
                                  <span className={`${metadata?.dangerous ? 'text-red-600' : 'text-gray-700'}`}>
                                    {metadata?.name}
                                    {metadata?.dangerous && ' ⚠️'}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4 pt-6 border-t">
                <button
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                {selectedPermissions.length > 0 && (
                  <button
                    onClick={handleStartSimulation}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Start Simulation
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PermissionSimulator;